// Structural evaluators for a fixed attribute order (corrected metrics only): the contingency tree is built once,
// a configuration is a "solution" (one Int32Array per attribute: category base index -> position), and the
// category ordering-dependent metrics are computed without clipping:
//   - Number of Crossings: sum of pairwise terms between the category orders of two attributes (CrossingModel);
//   - Overlap: w1 * w2 * H / |d1 - d2| per crossing pair (d = horizontal displacement of a ribbon);
//   - Slope, Orthogonality: sums over the ribbons; Crossing Angle: mean over the crossing pairs.
// Derivation: results/formulations/crossing_decomposition.md in the parent repository. The reference path is
// index.js (computeLayout + corrected.js); test/structural.test.js checks the equality.
import { DEFAULT_LAYOUT_OPTIONS } from '../layout/layout.js';
import { METRICS } from './directions.js';

// As in layout.js (not exported there).
const Y0 = 45;
const BOTTOM_MARGIN = 2;
// As in shared.js and corrected.js.
const SLOPE_EPSILON = 0.00000001;
const RAD_TO_DEG = 180 / Math.PI;
const BEST_CROSSING_ANGLE = 90;

export const PAIR_WEIGHTS = {
    unit: () => 1,
    product: (a, b) => a * b,
    sum: (a, b) => a + b,
    harmonic: (a, b) => a * b / (a + b),
};

export class StructuralEvaluator {
    /**
     * @param dataset         dataset from data/dataset-loader.js
     * @param attributeOrder  [{ name, categories }] with the categories in base order (config-id.js)
     * @param layoutOptions   { width, height, spacing }
     */
    constructor(dataset, attributeOrder, layoutOptions = {}) {
        const { width, height, spacing } = { ...DEFAULT_LAYOUT_OPTIONS, ...layoutOptions };
        this.width = width;
        this.height = height;
        this.spacing = spacing;
        this.attributes = attributeOrder.map(a => ({ name: a.name, categories: a.categories.slice() }));
        const nd = this.nd = this.attributes.length;
        const sizes = this.sizes = this.attributes.map(a => a.categories.length);
        // Offsets of the attributes in a flat position array.
        this.offsets = [];
        let flat = 0;
        for (let k = 0; k < nd; k++) { this.offsets.push(flat); flat += sizes[k]; }
        this.flat = new Int32Array(flat);

        // Contingency tree as flat arrays; node = path of categories (depth = index of its last attribute).
        const catIndex = this.attributes.map(a => new Map(a.categories.map((c, i) => [c, i])));
        const depth = [], cat = [], count = [], parent = [], children = [];
        const rootChildren = new Int32Array(sizes[0]).fill(-1);
        const addNode = (d, c, p) => {
            depth.push(d); cat.push(c); count.push(0); parent.push(p);
            children.push(d + 1 < nd ? new Int32Array(sizes[d + 1]).fill(-1) : null);
            return depth.length - 1;
        };
        let total = 0;
        const catCount = sizes.map(n => new Float64Array(n));
        for (const row of dataset.rows) {
            const cats = this.attributes.map((a, k) => catIndex[k].get(row[a.name]));
            if (cats.some(c => c === undefined)) continue;
            total++;
            let node = -1;
            for (let k = 0; k < nd; k++) {
                const table = node < 0 ? rootChildren : children[node];
                if (table[cats[k]] < 0) table[cats[k]] = addNode(k, cats[k], node);
                node = table[cats[k]];
                count[node]++;
                catCount[k][cats[k]]++;
            }
        }
        this.total = total;
        this.numNodes = depth.length;
        this.nodeDepth = Int32Array.from(depth);
        this.nodeCat = Int32Array.from(cat);
        this.nodeCount = Float64Array.from(count);
        this.nodeParent = Int32Array.from(parent);
        this.children = children;
        this.rootChildren = rootChildren;
        this.nodeDx = this.nodeCount.map(c => c / total * (width - spacing));
        this.catDx = catCount.map(counts => counts.map(c => c / total * (width - spacing)));
        const dy = (height - Y0 - BOTTOM_MARGIN) / (nd - 1);
        this.y = this.attributes.map((a, i) => Y0 + i * dy);
        this.bandHeight = [];
        for (let k = 0; k + 1 < nd; k++) this.bandHeight.push(Math.abs(this.y[k] - this.y[k + 1]));

        // Ribbons = nodes of depth >= 1; band = depth - 1.
        this.numRibbons = 0;
        for (let i = 0; i < this.numNodes; i++) if (depth[i] >= 1) this.numRibbons++;

        // Category path per node (flat, nd entries per node) and the ribbons per band, for forEachPair.
        this.paths = new Int16Array(this.numNodes * nd);
        for (let i = 0; i < this.numNodes; i++) {
            for (let n = i; n >= 0; n = parent[n]) this.paths[i * nd + depth[n]] = cat[n];
        }
        const byBand = Array.from({ length: nd - 1 }, () => []);
        for (let i = 0; i < this.numNodes; i++) if (depth[i] >= 1) byBand[depth[i] - 1].push(i);
        this.byBand = byBand.map(nodes => Int32Array.from(nodes));
        // Pair arrays of the pair-based metrics (Overlap, Crossing Angle); built on first use (ensurePairs), since
        // their number grows with the square of the ribbons of a band.
        this.pairs = null;

        // Layout state, indexed by node.
        this.sx = new Float64Array(this.numNodes);          // x of the top left corner (upper axis)
        this.tx = new Float64Array(this.numNodes);          // x of the bottom left corner (lower axis)
        this.dfs = new Int32Array(this.numRibbons);         // ribbons in the order of computeLayout
        this.slopes = new Float64Array(this.numNodes);
        this.directions = new Int8Array(this.numNodes);
        this.order = sizes.map(n => new Int32Array(n));
        this.catX = sizes.map(n => new Float64Array(n));
        this.inOffset = sizes.map(n => new Float64Array(n));
        this.outOffset = sizes.map(n => new Float64Array(n));
        this.models = new Map();
    }

    /**
     * Ribbon pairs of one band that can cross: top order decided by attribute t (categories a1, a2), bottom order
     * by attribute b (categories c1, c2), t != b. Ribbon i has a1 and c1. Nothing is stored.
     * @param callback  (i, j, t, a1, a2, b, c1, c2, band)
     */
    forEachPair(callback) {
        const { nd, paths, byBand } = this;
        for (let band = 0; band + 1 < nd; band++) {
            const nodes = byBand[band], last = band + 1;
            for (let x = 0; x < nodes.length; x++) {
                const i = nodes[x], p = i * nd;
                for (let z = x + 1; z < nodes.length; z++) {
                    const j = nodes[z], q = j * nd;
                    let f = 0;
                    while (f < last && paths[p + f] === paths[q + f]) f++;
                    if (f === last) continue;                           // same parent: no crossing
                    const t = paths[p + band] !== paths[q + band] ? band : f;
                    const b = paths[p + last] !== paths[q + last] ? last : f;
                    if (t === b) continue;
                    callback(i, j, t, paths[p + t], paths[q + t], b, paths[p + b], paths[q + b], band);
                }
            }
        }
    }

    // The pairs of forEachPair as typed arrays; built once.
    ensurePairs() {
        if (this.pairs) return this.pairs;
        const pI = [], pJ = [], pT = [], pA1 = [], pA2 = [], pB = [], pC1 = [], pC2 = [], pArea = [];
        this.forEachPair((i, j, t, a1, a2, b, c1, c2, band) => {
            pI.push(i); pJ.push(j);
            pT.push(t); pA1.push(a1); pA2.push(a2);
            pB.push(b); pC1.push(c1); pC2.push(c2);
            pArea.push(this.nodeDx[i] * this.nodeDx[j] * this.bandHeight[band]);
        });
        const pairT = Int32Array.from(pT), pairB = Int32Array.from(pB);
        this.pairs = {
            numPairs: pI.length,
            pairI: Int32Array.from(pI), pairJ: Int32Array.from(pJ),
            pairT, pairA1: Int32Array.from(pA1), pairA2: Int32Array.from(pA2),
            pairB, pairC1: Int32Array.from(pC1), pairC2: Int32Array.from(pC2),
            // w1 * w2 * H of the pair (px^3); the overlap area of a crossing pair is this value / |d1 - d2|.
            pairArea: Float64Array.from(pArea),
            // Indices into the flat position array.
            pairTop1: pairT.map((t, n) => this.offsets[t] + pA1[n]),
            pairTop2: pairT.map((t, n) => this.offsets[t] + pA2[n]),
            pairBottom1: pairB.map((b, n) => this.offsets[b] + pC1[n]),
            pairBottom2: pairB.map((b, n) => this.offsets[b] + pC2[n]),
        };
        return this.pairs;
    }

    // Crossing model for a pair weight: key of PAIR_WEIGHTS or function (count1, count2, node1, node2).
    crossingModel(weight = 'unit') {
        if (typeof weight === 'function') return new CrossingModel(this, weight);
        if (!this.models.has(weight)) this.models.set(weight, new CrossingModel(this, PAIR_WEIGHTS[weight]));
        return this.models.get(weight);
    }

    /**
     * Ribbon geometry of a solution in O(nodes); fills sx and tx (per node) and dfs (ribbon order of computeLayout).
     */
    layout(solution) {
        const { nd, sizes, order, catX, catDx, inOffset, outOffset, children, nodeDx, sx, tx, dfs } = this;
        for (let k = 0; k < nd; k++) {
            const n = sizes[k], pos = solution[k], ord = order[k];
            for (let c = 0; c < n; c++) ord[pos[c]] = c;
            const padding = this.spacing / (n - 1);
            let x = 0;
            for (let p = 0; p < n; p++) {
                const c = ord[p];
                catX[k][c] = x;
                x += catDx[k][c] + padding;
            }
            inOffset[k].fill(0);
            outOffset[k].fill(0);
        }
        let r = 0;
        const recurse = (node, parentCat, d) => {
            const ord = order[d], table = children[node], n = sizes[d];
            for (let p = 0; p < n; p++) {
                const c = ord[p];
                const child = table[c];
                if (child < 0) continue;
                const dx = nodeDx[child];
                tx[child] = catX[d][c] + inOffset[d][c];
                inOffset[d][c] += dx;
                sx[child] = catX[d - 1][parentCat] + outOffset[d - 1][parentCat];
                outOffset[d - 1][parentCat] += dx;
                dfs[r++] = child;
                if (d + 1 < nd) recurse(child, c, d + 1);
            }
        };
        for (let p = 0; p < sizes[0]; p++) {
            const c = order[0][p];
            const child = this.rootChildren[c];
            if (child >= 0) recurse(child, c, 1);
        }
        return this;
    }

    // Ribbons of a solution in the format of computeLayout (for tests and rendering).
    ribbons(solution) {
        this.layout(solution);
        const result = [];
        for (let r = 0; r < this.numRibbons; r++) {
            const node = this.dfs[r];
            const d = this.nodeDepth[node];
            const path = new Array(d + 1);
            for (let n = node; n >= 0; n = this.nodeParent[n]) {
                path[this.nodeDepth[n]] = this.attributes[this.nodeDepth[n]].categories[this.nodeCat[n]];
            }
            const dx = this.nodeDx[node], sx = this.sx[node], tx = this.tx[node], sy = this.y[d - 1], ty = this.y[d];
            result.push({
                band: d - 1, path, count: this.nodeCount[node], dx,
                tl: [sx, sy], bl: [tx, ty], br: [tx + dx, ty], tr: [sx + dx, sy],
            });
        }
        return result;
    }

    // Slope angle (radians) and direction per ribbon from the current layout.
    computeSlopes() {
        const { numNodes, nodeDepth, sx, tx, slopes, directions, bandHeight } = this;
        for (let i = 0; i < numNodes; i++) {
            const d = nodeDepth[i];
            if (d === 0) continue;
            const alpha = Math.atan(Math.abs(sx[i] - tx[i]) / bandHeight[d - 1]);
            slopes[i] = alpha < SLOPE_EPSILON ? 0 : alpha;
            directions[i] = slopes[i] === 0 ? 0 : (sx[i] < tx[i] ? 1 : -1);
        }
    }

    flatten(solution) {
        const { nd, offsets, flat } = this;
        for (let k = 0; k < nd; k++) flat.set(solution[k], offsets[k]);
        return flat;
    }

    // Number of crossing ribbon pairs (integer).
    crossingsAbs(solution) {
        return this.crossingModel('unit').crossings(solution);
    }

    crossings(solution) {
        return this.crossingsAbs(solution) / this.numRibbons;
    }

    overlap(solution) {
        this.layout(solution);
        const P = this.flatten(solution);
        const { numPairs, pairI, pairJ, pairTop1, pairTop2, pairBottom1, pairBottom2, pairArea } = this.ensurePairs();
        const { sx, tx } = this;
        let sum = 0;
        for (let n = 0; n < numPairs; n++) {
            if ((P[pairTop1[n]] < P[pairTop2[n]]) === (P[pairBottom1[n]] < P[pairBottom2[n]])) continue;
            const i = pairI[n], j = pairJ[n];
            sum += pairArea[n] / Math.abs((tx[i] - sx[i]) - (tx[j] - sx[j]));
        }
        return sum / (this.width * this.height);
    }

    slope(solution) {
        this.layout(solution);
        this.computeSlopes();
        let sum = 0;
        for (let r = 0; r < this.numRibbons; r++) sum += this.slopes[this.dfs[r]];
        return sum * RAD_TO_DEG / this.numRibbons;
    }

    orthogonality(solution) {
        this.layout(solution);
        this.computeSlopes();
        let sloped = 0;
        for (let r = 0; r < this.numRibbons; r++) if (this.slopes[this.dfs[r]] !== 0) sloped++;
        return sloped / this.numRibbons;
    }

    crossingAngle(solution) {
        this.layout(solution);
        this.computeSlopes();
        const P = this.flatten(solution);
        const { numPairs, pairI, pairJ, pairTop1, pairTop2, pairBottom1, pairBottom2 } = this.ensurePairs();
        const { slopes, directions } = this;
        let sum = 0, crossing = 0;
        for (let n = 0; n < numPairs; n++) {
            if ((P[pairTop1[n]] < P[pairTop2[n]]) === (P[pairBottom1[n]] < P[pairBottom2[n]])) continue;
            const i = pairI[n], j = pairJ[n];
            const delta = (directions[i] * directions[j] === -1 ? slopes[i] + slopes[j] : Math.abs(slopes[i] - slopes[j])) * RAD_TO_DEG;
            sum += Math.min(delta, 180 - delta);
            crossing++;
        }
        return crossing === 0 ? BEST_CROSSING_ANGLE : sum / crossing;
    }

    // The five category ordering-dependent metrics in one pass (columns of correctedMetrics).
    evaluateAll(solution) {
        this.layout(solution);
        this.computeSlopes();
        const P = this.flatten(solution);
        const { numPairs, pairI, pairJ, pairTop1, pairTop2, pairBottom1, pairBottom2, pairArea } = this.ensurePairs();
        const { sx, tx, slopes, directions } = this;
        let area = 0, angles = 0, crossing = 0;
        for (let n = 0; n < numPairs; n++) {
            if ((P[pairTop1[n]] < P[pairTop2[n]]) === (P[pairBottom1[n]] < P[pairBottom2[n]])) continue;
            const i = pairI[n], j = pairJ[n];
            area += pairArea[n] / Math.abs((tx[i] - sx[i]) - (tx[j] - sx[j]));
            const delta = (directions[i] * directions[j] === -1 ? slopes[i] + slopes[j] : Math.abs(slopes[i] - slopes[j])) * RAD_TO_DEG;
            angles += Math.min(delta, 180 - delta);
            crossing++;
        }
        let slopeSum = 0, sloped = 0;
        for (let r = 0; r < this.numRibbons; r++) {
            const s = slopes[this.dfs[r]];
            slopeSum += s;
            if (s !== 0) sloped++;
        }
        return {
            overlap: area / (this.width * this.height),
            slope: slopeSum * RAD_TO_DEG / this.numRibbons,
            orthogonality: sloped / this.numRibbons,
            crossings: crossing / this.numRibbons,
            crossingsAbs: crossing,
            crossingAngle: crossing === 0 ? BEST_CROSSING_ANGLE : angles / crossing,
        };
    }

    // Value of one metric (key of METRICS, category ordering-dependent) for a solution.
    evaluate(metric, solution) {
        switch (metric) {
            case 'crossings': return this.crossings(solution);
            case 'overlap': return this.overlap(solution);
            case 'slope': return this.slope(solution);
            case 'orthogonality': return this.orthogonality(solution);
            case 'crossingAngle': return this.crossingAngle(solution);
            default:
                throw new Error(METRICS[metric] ? 'Not a category ordering-dependent metric: ' + metric : 'Unknown metric: ' + metric);
        }
    }
}

// Pair arrays as properties of the evaluator (optimizers, experiment scripts, tests).
for (const name of ['numPairs', 'pairI', 'pairJ', 'pairT', 'pairA1', 'pairA2', 'pairB', 'pairC1', 'pairC2', 'pairArea', 'pairTop1', 'pairTop2', 'pairBottom1', 'pairBottom2']) {
    Object.defineProperty(StructuralEvaluator.prototype, name, { get() { return this.ensurePairs()[name]; } });
}

/**
 * Pairwise crossing model: C(solution) = sum_{t < b} F_tb(order_t, order_b) with a weight tensor per attribute pair,
 *   weights[t][b][a1, a2, c1, c2], a1 < a2, c1 != c2 (base indices)
 *     = weight of the ribbon pairs whose top order is decided by a1, a2 of attribute t and whose bottom order by
 *       c1, c2 of attribute b; the pair crosses iff (a1 before a2) != (c1 before c2).
 */
export class CrossingModel {
    constructor(evaluator, weightFn = PAIR_WEIGHTS.unit) {
        const nd = this.nd = evaluator.nd;
        const sizes = this.sizes = evaluator.sizes;
        this.numRibbons = evaluator.numRibbons;
        this.weights = [];
        for (let t = 0; t < nd; t++) {
            this.weights.push([]);
            for (let b = 0; b < nd; b++) {
                this.weights[t].push(t < b ? new Float64Array(sizes[t] * sizes[t] * sizes[b] * sizes[b]) : null);
            }
        }
        const e = evaluator;
        // One pass over the pairs, in the order of ensurePairs; the pairs are not stored.
        e.forEachPair((i, j, t, a1, a2, b, c1, c2) => {
            this.add(t, a1, a2, b, c1, c2, weightFn(e.nodeCount[i], e.nodeCount[j], i, j));
        });
        // Free-free bound per attribute pair (constant): cheaper orientation per (a1, a2, c1, c2).
        this.freeBound = this.weights.map((row, t) => row.map((w, b) => {
            if (!w) return 0;
            const nt = sizes[t], nb = sizes[b];
            let c = 0;
            for (let a1 = 0; a1 < nt; a1++) for (let a2 = a1 + 1; a2 < nt; a2++) {
                const base = (a1 * nt + a2) * nb;
                for (let c1 = 0; c1 < nb; c1++) for (let c2 = c1 + 1; c2 < nb; c2++) {
                    c += Math.min(w[(base + c1) * nb + c2], w[(base + c2) * nb + c1]);
                }
            }
            return c;
        }));
    }

    add(t, a1, a2, b, c1, c2, w) {
        // The crossing condition is symmetric in the two attributes; store under (min, max).
        if (t > b) { [t, b] = [b, t]; [a1, c1] = [c1, a1]; [a2, c2] = [c2, a2]; }
        if (a1 > a2) { [a1, a2] = [a2, a1]; [c1, c2] = [c2, c1]; }
        const nt = this.sizes[t], nb = this.sizes[b];
        this.weights[t][b][((a1 * nt + a2) * nb + c1) * nb + c2] += w;
    }

    // Weighted crossings between attributes t < b.
    pairTerm(t, b, pos) {
        const nt = this.sizes[t], nb = this.sizes[b], w = this.weights[t][b], pt = pos[t], pb = pos[b];
        let c = 0;
        for (let a1 = 0; a1 < nt; a1++) {
            for (let a2 = a1 + 1; a2 < nt; a2++) {
                const top = pt[a1] < pt[a2];
                const base = (a1 * nt + a2) * nb;
                for (let c1 = 0; c1 < nb; c1++) {
                    const bottomBase = (base + c1) * nb;
                    for (let c2 = 0; c2 < nb; c2++) {
                        if (c1 === c2) continue;
                        const x = w[bottomBase + c2];
                        if (x !== 0 && top !== (pb[c1] < pb[c2])) c += x;
                    }
                }
            }
        }
        return c;
    }

    crossings(pos) {
        let c = 0;
        for (let t = 0; t < this.nd; t++) for (let b = t + 1; b < this.nd; b++) c += this.pairTerm(t, b, pos);
        return c;
    }

    // Terms involving attribute k only (for moves of attribute k).
    crossingsInvolving(k, pos) {
        let c = 0;
        for (let t = 0; t < this.nd; t++) {
            if (t === k) continue;
            c += t < k ? this.pairTerm(t, k, pos) : this.pairTerm(k, t, pos);
        }
        return c;
    }

    /**
     * Context of the lower bound under constraints (todo/17): forced[k] = Int8Array (c1 * n + c2) with +1 if c1
     * precedes c2 in every admissible order of attribute k, -1 if it follows in every one, 0 otherwise; null for an
     * attribute without forced pairs. Such a pair contributes its forced orientation instead of the cheaper one;
     * the bound stays valid, since no admissible completion has the other orientation.
     */
    constrainedBound(forced) {
        const { sizes } = this;
        const freeBound = this.freeBound.map((row, t) => row.map((value, b) => {
            const ft = forced[t], fb = forced[b], w = this.weights[t][b];
            if (!w || (!ft && !fb)) return value;
            const nt = sizes[t], nb = sizes[b];
            let c = 0;
            for (let a1 = 0; a1 < nt; a1++) for (let a2 = a1 + 1; a2 < nt; a2++) {
                const base = (a1 * nt + a2) * nb;
                const top = ft ? ft[a1 * nt + a2] : 0; // +1: a1 before a2
                for (let c1 = 0; c1 < nb; c1++) for (let c2 = c1 + 1; c2 < nb; c2++) {
                    const x12 = w[(base + c1) * nb + c2], x21 = w[(base + c2) * nb + c1];
                    const bottom = fb ? fb[c1 * nb + c2] : 0; // +1: c1 before c2
                    // Crossing weight for (a1 before a2, c1 before c2): same orientation -> x21, else x12.
                    let m = Infinity;
                    for (const tp of top ? [top > 0] : [true, false]) {
                        for (const bp of bottom ? [bottom > 0] : [true, false]) m = Math.min(m, tp === bp ? x21 : x12);
                    }
                    c += m;
                }
            }
            return c;
        }));
        return { forced, freeBound };
    }

    /**
     * Lower bound for a partial assignment; fixed = boolean per attribute.
     * strength: 'fixed'    = exact terms of the pairs with both attributes fixed;
     *           'oneSided' = plus the one-sided bound for pairs with one fixed attribute (cheaper orientation
     *                        per unordered category pair of the free attribute);
     *           'both'     = plus the cheaper orientation per (a1, a2, c1, c2) for pairs of free attributes.
     * context:  null (unconstrained) or the result of constrainedBound: forced pairs take their orientation.
     */
    lowerBound(pos, fixed, strength = 'both', context = null) {
        let c = 0;
        const nd = this.nd;
        const forced = context ? context.forced : null, freeBound = context ? context.freeBound : this.freeBound;
        for (let t = 0; t < nd; t++) {
            for (let b = t + 1; b < nd; b++) {
                if (fixed[t] && fixed[b]) { c += this.pairTerm(t, b, pos); continue; }
                if (strength === 'fixed') continue;
                const nt = this.sizes[t], nb = this.sizes[b], w = this.weights[t][b], pt = pos[t], pb = pos[b];
                if (fixed[t]) {
                    const f = forced && forced[b];
                    for (let c1 = 0; c1 < nb; c1++) for (let c2 = c1 + 1; c2 < nb; c2++) {
                        let before = 0, after = 0; // crossings if c1 before c2 / c2 before c1
                        for (let a1 = 0; a1 < nt; a1++) for (let a2 = a1 + 1; a2 < nt; a2++) {
                            const base = (a1 * nt + a2) * nb;
                            const x12 = w[(base + c1) * nb + c2], x21 = w[(base + c2) * nb + c1];
                            if (pt[a1] < pt[a2]) { before += x21; after += x12; } else { before += x12; after += x21; }
                        }
                        const o = f ? f[c1 * nb + c2] : 0;
                        c += o > 0 ? before : o < 0 ? after : Math.min(before, after);
                    }
                } else if (fixed[b]) {
                    const f = forced && forced[t];
                    for (let a1 = 0; a1 < nt; a1++) for (let a2 = a1 + 1; a2 < nt; a2++) {
                        let before = 0, after = 0; // a1 before a2 / a2 before a1
                        const base = (a1 * nt + a2) * nb;
                        for (let c1 = 0; c1 < nb; c1++) for (let c2 = 0; c2 < nb; c2++) {
                            if (c1 === c2) continue;
                            const x = w[(base + c1) * nb + c2];
                            if (x === 0) continue;
                            if (pb[c1] < pb[c2]) after += x; else before += x;
                        }
                        const o = f ? f[a1 * nt + a2] : 0;
                        c += o > 0 ? before : o < 0 ? after : Math.min(before, after);
                    }
                } else if (strength === 'both') {
                    c += freeBound[t][b];
                }
            }
        }
        return c;
    }
}
